<template>
  <v-container>
    <v-row>
      <v-spacer />
      <v-btn @click="removePhoto" color="red" class="mr-3 mb-2" variant="text"
        >Remove</v-btn
      >
    </v-row>
    <v-row class="ml-7 mr-0 mt-2">
      <v-col cols="6" class="py-0 pl-0 pr-1">
        <v-select
          v-model="sideSelection"
          :items="sideOptions"
          label="Side"
          variant="outlined"
          density="compact"
        />
      </v-col>
      <v-col cols="6" class="py-0 pl-1 pr-0">
        <v-select
          v-model="viewSelection"
          :items="viewOptions"
          label="View"
          variant="outlined"
          density="compact"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-file-input
        class="ml-2 mr-3 mt-n4"
        chips
        v-bind="{ accept: 'image/png, image/jpeg, image/tiff' }"
        @update:model-value="setFile"
        :error-messages="fileError"
        label="Click to select image file to upload"
        variant="outlined"
      />
    </v-row>
  </v-container>
</template>

<script lang="ts">
import { defineComponent, ref, watch, computed, ComputedRef } from 'vue';
import { TextPhoto } from '@oare/types';

// The server accepts photo files up to 25 MB; nginx allows 30M for the whole
// multipart request, so a file that passes this check is never cut by nginx.
const MAX_PHOTO_BYTES = 25 * 1024 * 1024;

export default defineComponent({
  props: {
    uuid: {
      type: String,
      required: true,
    },
  },
  emits: ['export-photo', 'remove-photo'],
  setup(props, { emit }) {
    const localPhotoUrl = ref<string>();
    const localUpload = ref<File>();
    const fileError = ref('');
    const setFile = async (files: File | File[] | null) => {
      const upload = Array.isArray(files) ? files[0] : files;
      fileError.value = '';
      if (!upload) {
        localUpload.value = undefined;
        localPhotoUrl.value = undefined;
        return;
      }
      if (upload.size > MAX_PHOTO_BYTES) {
        localUpload.value = undefined;
        localPhotoUrl.value = undefined;
        fileError.value = `This photo is ${(upload.size / 1024 / 1024).toFixed(
          1
        )} MB. Photos can be at most 25 MB; please choose a smaller file.`;
        return;
      }
      localUpload.value = upload;
      localPhotoUrl.value = await readURL(upload);
    };

    const readURL = (file: File): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = event => reject(event);
        reader.readAsDataURL(file);
      });
    };

    const sideSelection = ref<number | string>();
    const viewSelection = ref<string>();

    const sideOptions = ref<{ title: string; value: string | number }[]>([
      {
        title: 'obv.',
        value: 1,
      },
      {
        title: 'lo.e.',
        value: 2,
      },
      {
        title: 'rev.',
        value: 3,
      },
      {
        title: 'u.e.',
        value: 4,
      },
      {
        title: 'le.e.',
        value: 5,
      },
      {
        title: 'r.e.',
        value: 6,
      },
      {
        title: 'Envelope Inner obv.',
        value: 'a',
      },
      {
        title: 'Envelope Inner lo.e.',
        value: 'b',
      },
      {
        title: 'Envelope Inner rev.',
        value: 'c',
      },
      {
        title: 'Envelope Inner u.e.',
        value: 'd',
      },
      {
        title: 'Envelope Inner le.e.',
        value: 'e',
      },
      {
        title: 'Envelope Inner r.e.',
        value: 'f',
      },
      {
        title: 'Fat Cross',
        value: 'x',
      },
      {
        title: 'Handcopy',
        value: 'h',
      },
      {
        title: 'Unknown Side',
        value: 8,
      },
      {
        title: 'Unknown Edge',
        value: 9,
      },
    ]);

    const viewOptions = ref<{ title: string; value: string }[]>([
      {
        title: 'Upper (from above)',
        value: 'u',
      },
      {
        title: 'Direct',
        value: 'd',
      },
      {
        title: 'Lower',
        value: 'l',
      },
      {
        title: 'Inverted Direct',
        value: 'i',
      },
      {
        title: 'Inverted Upper',
        value: 'h',
      },
      {
        title: 'Inverted Lower',
        value: 'j',
      },
      {
        title: 'View for Scale',
        value: 'z',
      },
      {
        title: 'Emphasis on Name',
        value: 'n',
      },
      {
        title: 'Seal Impression Focus',
        value: 's',
      },
      {
        title: 'From Right',
        value: 'fr',
      },
      {
        title: 'From Left',
        value: 'fl',
      },
      {
        title: 'Contextual',
        value: 'cx',
      },
      {
        title: 'Detail Unspecified',
        value: 'du',
      },
    ]);

    const photo: ComputedRef<TextPhoto | null> = computed(() => {
      if (
        localPhotoUrl.value &&
        sideSelection.value &&
        viewSelection.value &&
        localUpload.value
      ) {
        return {
          uuid: props.uuid,
          url: localPhotoUrl.value,
          side: sideSelection.value,
          view: viewSelection.value,
          upload: localUpload.value,
        };
      } else {
        return null;
      }
    });

    // An incomplete photo (no file, side or view) is exported with only its
    // uuid, so the parent drops a file that was rejected or cleared instead
    // of keeping the previous one.
    watch(photo, () => {
      emit('export-photo', photo.value || { uuid: props.uuid });
    });

    const removePhoto = () => {
      emit('remove-photo');
    };

    return {
      setFile,
      fileError,
      localPhotoUrl,
      sideOptions,
      viewOptions,
      sideSelection,
      viewSelection,
      removePhoto,
    };
  },
});
</script>
